import { QueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import type { Frame } from "@/types/dragon-tiles";

import {
  dragonTilesByTypeQueryOptions,
  frameQueryOptions,
} from "@/api/dragon-tiles";
import { BYOC_PARENT_TILE_ID } from "@/features/Connectors/byocConstants";
import { buildByocHydratedConnector } from "@/features/Connectors/hydrateByocConnector";
import FrameManager from "@/features/FrameManager/FrameManager";
import {
  hasElevatedAccessValue,
  usePrivilegedModeStore,
} from "@/utils/privilegedMode";

type FrameRouteLoaderData = {
  frame: Frame;
  siteId: string;
  connectorId: string;
  dragonTileId: string;
};

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/byoc/tile/$frameId",
)({
  component: RouteComponent,
  beforeLoad: ({ context, params }) => {
    const privilegedModeDisabled = usePrivilegedModeStore.getState().disabled;
    const hasElevatedAccess = hasElevatedAccessValue(
      context.auth?.user?.user.role,
      privilegedModeDisabled,
    );

    if (!hasElevatedAccess) {
      throw redirect({
        to: "/dragon-tiles/$siteId",
        params: { siteId: params.siteId },
      });
    }
  },
  loader: async ({ params, context }): Promise<FrameRouteLoaderData> => {
    const { queryClient } = context as { queryClient: QueryClient };
    const { siteId, frameId } = params;

    const byocDragonTiles = await queryClient.ensureQueryData(
      dragonTilesByTypeQueryOptions(siteId, "byoc"),
    );

    if (byocDragonTiles.length === 0) {
      throw redirect({
        to: "/dragon-tiles/$siteId",
        params: { siteId },
      });
    }

    const hydrated = buildByocHydratedConnector({
      byocDragonTiles,
    });

    const frame = await queryClient.fetchQuery(
      frameQueryOptions(siteId, frameId),
    );
    if (!frame) {
      throw new Error("Tile not found");
    }

    return {
      frame,
      siteId,
      connectorId: BYOC_PARENT_TILE_ID,
      dragonTileId: hydrated.dragonTile.id,
    };
  },
});

function RouteComponent() {
  const { frame, siteId, connectorId, dragonTileId } = Route.useLoaderData();

  if (!frame || !siteId) {
    throw new Error("Required data not found");
  }

  return (
    <FrameManager
      siteId={siteId}
      parentTileId={connectorId}
      frameParentTileId={dragonTileId}
      returnTo="connector-tile"
      connectorParentTileId={connectorId}
    />
  );
}
